import { useState } from "react";
import { campaigns as initialCampaigns } from "../../../mocks/campaigns";
import type { Campaign } from "../../../mocks/campaigns";
import CampaignFormModal from "../components/CampaignFormModal";
import ConfirmModal from "../components/ConfirmModal";

export default function CampaignsSection() {
  const [campaignList, setCampaignList] = useState<Campaign[]>(initialCampaigns);
  const [search, setSearch] = useState("");
  const [paused, setPaused] = useState<Set<string>>(new Set());

  type ModalState =
    | { type: "none" }
    | { type: "add" }
    | { type: "edit"; campaign: Campaign }
    | { type: "delete"; campaign: Campaign };

  const [modal, setModal] = useState<ModalState>({ type: "none" });

  const filtered = campaignList.filter((c) =>
    c.title.toLowerCase().includes(search.toLowerCase()) ||
    c.venueName.toLowerCase().includes(search.toLowerCase())
  );

  const togglePause = (id: string) =>
    setPaused((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const handleAdd = (data: Partial<Campaign>) => {
    const newCamp: Campaign = {
      id: `camp-${Date.now()}`,
      title: data.title ?? "",
      venueName: data.venueName ?? "",
      venueCategory: data.venueCategory ?? "",
      discountPct: data.discountPct ?? 10,
      description: data.description ?? "",
      image: data.image ?? "https://public.readdy.ai/ai/img_res/bb23bb6ede1d59f6783e6b4e40da622e.jpg",
      expiresDate: data.expiresDate ?? "",
      couponCode: data.couponCode ?? "",
      isSponsored: data.isSponsored ?? false,
      district: data.district ?? "Osmangazi",
    };
    setCampaignList((prev) => [newCamp, ...prev]);
    setModal({ type: "none" });
  };

  const handleEdit = (data: Partial<Campaign>) => {
    if (modal.type !== "edit") return;
    setCampaignList((prev) => prev.map((c) =>
      c.id === modal.campaign.id ? { ...c, ...data, id: c.id } : c
    ));
    setModal({ type: "none" });
  };

  const handleDelete = () => {
    if (modal.type !== "delete") return;
    setCampaignList((prev) => prev.filter((c) => c.id !== modal.campaign.id));
    setModal({ type: "none" });
  };

  return (
    <div className="space-y-5">
      {/* Toolbar */}
      <div className="bg-white rounded-2xl p-4 border border-stone-100 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Kampanya veya mekan ara..."
            className="w-full pl-9 pr-4 py-2 text-sm border border-stone-200 rounded-xl outline-none focus:border-emerald-400 transition-colors" />
        </div>
        <span className="text-xs text-stone-400 ml-auto">{filtered.length} kampanya</span>
        <button onClick={() => setModal({ type: "add" })}
          className="flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white px-4 py-2 rounded-xl text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap">
          <i className="ri-add-line" /> Kampanya Ekle
        </button>
      </div>

      {/* Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {filtered.map((camp) => {
          const isPaused = paused.has(camp.id);
          return (
            <div key={camp.id} className={`bg-white rounded-2xl border overflow-hidden transition-all ${isPaused ? "border-stone-200 opacity-70" : "border-stone-100"}`}>
              <div className="relative h-36 overflow-hidden">
                <img src={camp.image} alt={camp.title} className="w-full h-full object-cover object-top" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute top-3 left-3 flex items-center gap-2">
                  <span className="px-2.5 py-1 rounded-full bg-amber-500 text-white text-xs font-bold">
                    %{camp.discountPct} İNDİRİM
                  </span>
                  {isPaused && (
                    <span className="px-2.5 py-1 rounded-full bg-stone-600 text-white text-xs font-medium">Durduruldu</span>
                  )}
                </div>
                {camp.isSponsored && (
                  <div className="absolute top-3 right-3">
                    <span className="px-2.5 py-1 rounded-full bg-white/20 backdrop-blur-sm text-white text-xs font-medium">Sponsorlu</span>
                  </div>
                )}
                <div className="absolute bottom-3 left-3">
                  <p className="text-white font-bold text-sm">{camp.title}</p>
                  <p className="text-white/70 text-xs">{camp.venueName}</p>
                </div>
              </div>

              <div className="p-4">
                <p className="text-stone-500 text-xs leading-relaxed mb-3 line-clamp-2">{camp.description}</p>
                <div className="flex items-center gap-4 mb-3 text-xs text-stone-500">
                  <div className="flex items-center gap-1.5">
                    <i className="ri-time-line text-stone-400" />
                    Son: <strong className="text-stone-700">{camp.expiresDate}</strong>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <i className="ri-map-pin-line text-stone-400" />
                    {camp.district}
                  </div>
                </div>
                <div className="flex items-center gap-2 bg-stone-50 rounded-xl px-3 py-2 mb-3">
                  <i className="ri-coupon-line text-amber-600 text-sm" />
                  <span className="text-sm font-bold tracking-widest text-stone-700">{camp.couponCode || "—"}</span>
                  <span className="ml-auto text-xs text-stone-400">Kupon Kodu</span>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setModal({ type: "edit", campaign: camp })}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2 border border-stone-200 rounded-xl text-xs font-medium text-stone-600 hover:bg-stone-50 hover:border-emerald-300 hover:text-emerald-700 transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-edit-line" /> Düzenle
                  </button>
                  <button
                    onClick={() => togglePause(camp.id)}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-2 border rounded-xl text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      isPaused
                        ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                        : "border-stone-200 text-stone-600 hover:bg-stone-50"
                    }`}>
                    <i className={isPaused ? "ri-play-line" : "ri-pause-line"} />
                    {isPaused ? "Başlat" : "Durdur"}
                  </button>
                  <button
                    onClick={() => setModal({ type: "delete", campaign: camp })}
                    className="w-9 flex items-center justify-center py-2 border border-stone-200 rounded-xl text-xs text-stone-400 hover:text-red-600 hover:border-red-200 hover:bg-red-50 transition-colors cursor-pointer">
                    <i className="ri-delete-bin-line" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div className="bg-white rounded-2xl p-14 text-center border border-stone-100 text-stone-400 text-sm">
          Sonuç bulunamadı
        </div>
      )}

      {/* Modals */}
      {modal.type === "add" && (
        <CampaignFormModal mode="add" onSave={handleAdd} onClose={() => setModal({ type: "none" })} />
      )}
      {modal.type === "edit" && (
        <CampaignFormModal mode="edit" initial={modal.campaign} onSave={handleEdit} onClose={() => setModal({ type: "none" })} />
      )}
      {modal.type === "delete" && (
        <ConfirmModal
          title="Kampanyayı Sil"
          message={`"${modal.campaign.title}" kampanyasını kalıcı olarak silmek istediğinize emin misiniz?`}
          confirmLabel="Evet, Sil"
          onConfirm={handleDelete}
          onCancel={() => setModal({ type: "none" })}
        />
      )}
    </div>
  );
}
